import type { Brand } from "@borealis/layout-express";
import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";

type ExpertReviewData = {
  thumbnailUrl: string;
  title: string;
  reviewUrl: string;
  reviewDate: Date;
  specKeys?: string[];
  fuelType?: FuelType;
};

// Check the DAVS list to add more fuel type:
// https://davs-backoffice.platform.svc.finn.no/mobility/attribute/engine.fuel/257
enum FuelType {
  Petrol = 1,
  Diesel = 2,
  Electric = 4,
  HybridPetrol = 6,
  PlugInPetrol = 1352,
}

type ExpertReviewEntry = ExpertReviewData | ExpertReviewData[];
type BrandReviewMap = Partial<Record<Brand, ExpertReviewEntry>>;
type MakeModelMap = Record<number, BrandReviewMap>;
type MakeSeriesModelMap = Record<number, BrandReviewMap | Record<number, BrandReviewMap>>;
type CombinedReviewMap = Record<number, MakeModelMap | MakeSeriesModelMap>;

const isBrandReviewMap = (value: BrandReviewMap | Record<number, BrandReviewMap>): value is BrandReviewMap =>
  Object.keys(value).every((key) => Number.isNaN(Number(key)));

const pickBestReview = (entries: ExpertReviewEntry | undefined, year: number): ExpertReviewData | undefined => {
  if (!entries) return undefined;
  const list = Array.isArray(entries) ? entries : [entries];

  // Filter to reviews within the valid year range
  const valid = list.filter((r) => {
    const ry = r.reviewDate.getFullYear();
    return ry >= year - 1 && ry <= year + 2;
  });

  if (valid.length === 0) return undefined;

  // Pick the one closest to the listing year, preferring newer
  return valid.reduce((best, r) => {
    const bestDiff = Math.abs(best.reviewDate.getFullYear() - year);
    const rDiff = Math.abs(r.reviewDate.getFullYear() - year);
    if (rDiff < bestDiff) return r;
    if (rDiff === bestDiff && r.reviewDate > best.reviewDate) return r;
    return best;
  });
};

export const getReview = (
  brand: Brand,
  carModelTaxonomy: IAdViewManaged,
  year: number,
  modelSpec: string,
  fuelType: IAdViewManaged,
): ExpertReviewData | undefined => {
  if (!carModelTaxonomy.id || !carModelTaxonomy.parent?.id) {
    return;
  }
  const make = carModelTaxonomy.parent.parent?.id ? carModelTaxonomy.parent.parent.id : carModelTaxonomy.parent.id;
  const series = carModelTaxonomy.parent.parent?.id ? carModelTaxonomy.parent.id : null;
  const model = carModelTaxonomy.id;
  const resolvedFuelType = fuelType.id;
  const resolvedBrand = brand === "dba" ? "bilbasen" : brand;

  const makeData = reviews[make];
  if (!makeData) return;

  let entries: ExpertReviewEntry | undefined;

  if (series) {
    const seriesData = (makeData as MakeSeriesModelMap)[series];
    if (!seriesData) return;
    entries = isBrandReviewMap(seriesData) ? seriesData[resolvedBrand] : seriesData[model]?.[resolvedBrand];
  } else {
    entries = (makeData as MakeModelMap)[model]?.[resolvedBrand];
  }

  // Filter entries by specKeys and fuelType:
  // - If a review has specKeys, all of them must appear in the modelSpec string.
  // - If a review has a fuelType, it must match the resolvedFuelType.
  // Reviews without specKeys/fuelType are always eligible for those checks.
  if (entries) {
    const list = Array.isArray(entries) ? entries : [entries];
    const specLower = modelSpec.toLowerCase();
    const matched = list.filter(
      (r) =>
        (!r.specKeys || r.specKeys.every((key) => specLower.includes(key.toLowerCase()))) &&
        (!r.fuelType || r.fuelType === resolvedFuelType),
    );
    entries = matched.length === 0 ? undefined : matched.length === 1 ? matched[0] : matched;
  }

  return pickBestReview(entries, year);
};

// To add more reviews to this map, you need to find a mobility listing matching the make and model for the review aticle.
// Then lookup the listing in the adview-provider and find the correct make, series and model ID's using the car_model_taxonomy property
// Then add it to the map for the correct brand it belongs to. Copy the url of the lowest resolution image to use for thumbnail.
// If a make/model/brand already has a review, you can use an array to store multiple reviews.
// The best matching review will be selected based on the listing's model year (closest year, preferring newer).
const reviews: CombinedReviewMap = {
  824: {
    // Alpine
    938: {
      // A290
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/6eZ7ABE65TnXYgtW8mn5SQ/ae711057e8e646ad66a135f37ce3aee7/Alpine_A290_GTS_Dynamisk_11.jpeg?w=640&fm=webp&q=75",
        title: "Test: Alpine A290 - fransk kruttlapp med innebygget trener",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-alpine-a290-fransk-kruttlapp-med-innebygget-trener",
        reviewDate: new Date("2026-05-18"),
      },
    },
    2368: {
      // A390
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5CBWcYPYto3uIxQmgVDuGQ/6fb85fc3c7e1c8eedf72557111aa12ab/Alpine_A390_GT_Dynamisk_27.jpeg?w=640&fm=webp&q=75",
        title: "Test: Alpine A390 - Fransk Porsche, eller racer-Renault?",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-alpine-a390-fransk-porsche-eller-racer-renault",
        reviewDate: new Date("2026-05-13"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/6eRFadV8TEJsW4iDA0CKty/773ba3033ae0a4aa9957ace83cf8a98c/Alpine_A390_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Alpine A390 – identitetskrisen",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-alpine-a390-identitetskrisen",
        reviewDate: new Date("2026-05-19"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5EnnBAQ7OKJFW4UGiu4Aea/6ef228c3a4a5fb1ca1d0050766731765/Alpine_A390_GT_Dynamisk_29.jpeg?fm=webp&w=640&q=60",
        title: "Første tur: Hvad er det mon, Alpine A390 vil i Danmark?",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-hvad-er-det-mon-alpine-a390-vil-i-danmark",
        reviewDate: new Date("2026-05-17"),
      },
    },
  },
  744: {
    // Audi
    2495: {
      // e-tron GT-Serie
      2001146: {
        // S e-tron GT
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/4xBjDilWjznNYLCKQH6W7/c3765190d79aae1c6e81062689b12226/Audi_e-tron_GT__1_.jpeg?w=640&fm=webp&q=75",
          title: "Första turen: Audi S e-tron GT når nya höjder",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/foersta-turen-audi-s-e-tron-gt-nar-nya-hoejder",
          reviewDate: new Date("2025-01-30"),
        },
      },
    },
    2485: {
      // A5-Serie
      8314: {
        // A5
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/4z2qzikLApv3k0ZDcqP1D3/88721f2abf0fcf38d9bf0d332b7a82f8/Audi_A5__1_.jpg?w=640&fm=webp&q=75",
          title: "Första turen: Audi A5 är inte som du minns den",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/foersta-turen-audi-a5-aer-inte-som-du-minns-den",
          reviewDate: new Date("2025-01-30"),
        },
      },
      2000185: {
        // RS5
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/66p9VwbtbZGjyRgh0uG4F0/354565654f414198b0872b8499efab85/Audi_RS5_test-1.jpg?w=640&fm=webp&q=75",
          title: "Minitest: Audi RS 5 – lovar runt och håller tungt",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/minitest-audi-rs-5-lovar-runt-och-haller-tungt",
          reviewDate: new Date("2026-05-04"),
        },
      },
    },
    2492: {
      // A6-Serie
      840: {
        // A6
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/3WICQegixYfpfUXhNnbIyh/7e307a18047ef300a6313345373e1434/TEST_Audi_A6_TDI_MBB_481.jpeg?fm=webp&w=640&q=60",
          title: "Test: Audi A6 har muligvis den bedste firecylindrede diesel i verden - og det er ligegyldigt",
          reviewUrl:
            "https://blog.bilbasen.dk/test-audi-a6-har-muligvis-den-bedste-firecylindrede-diesel-i-verden-og-det",
          reviewDate: new Date("2026-01-24"),
        },
      },
      596: {
        // A6 e-tron
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/6da814f7-e41f-3742-82d5-f3ee6a627fcb/7d8887d92f4a7a531b1b8d61d2ef0410/TEST-Audi-A6-Avant-E-Tron-Performance-0048.jpeg?w=640&fm=webp&q=75",
          title: "Test: Audi A6 e-tron Avant er skabt til de lange ture",
          reviewUrl: "https://blog.bilbasen.dk/test-audi-a6-e-tron-avant-er-skabt-til-de-lange-ture-478850",
          reviewDate: new Date("2025-03-22"),
        },
      },
    },
    3568: {
      // Q4-Serie
      2000541: {
        // Q4 e-tron
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/7ahn8QG7fuy4Me4vkq93iq/64119885ef41f188e37a4cc6d8667e14/Audi_Q4_E-Tron_45_S_Line-06.jpeg?fm=webp&w=640&q=60",
          title: "Test: Audi Q4 E-Tron S Line Edition – mere Audi for pengene",
          reviewUrl: "https://blog.bilbasen.dk/test-audi-q4-e-tron-s-line-edition-mere-audi-for-pengene",
          reviewDate: new Date("2026-01-25"),
        },
      },
    },
    2489: {
      // Q5-Serie
      2000097: {
        // Q5
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/6fdf0291-3226-3183-aa85-3976431a75d5/52a793392e4534c604b20bd5512f2fd7/Audi-Q5-Cover.jpg?w=640&fm=webp&q=75",
          title: "Første tur: Audi Q5 kan stadig",
          reviewUrl: "https://blog.bilbasen.dk/foerste-tur-audi-q5-kan-stadig-478441",
          reviewDate: new Date("2025-03-07"),
        },
      },
    },
  },
  8101: {
    // BYD
    2000592: {
      // Atto 3
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/7IDASu9iMnCByxeb2Jysys/953b5716acb2b443dd58f0572265af19/BYD_Atto_3-20.jpg?w=640&fm=webp&q=75",
        title: "Test: Facelift fjerner fjolleriet fra BYD Atto 3",
        reviewUrl: "https://blog.bilbasen.dk/test-facelift-fjerner-fjolleriet-fra-byd-atto-3",
        reviewDate: new Date("2025-06-08"),
      },
    },
    3550: {
      // Atto 3 Evo
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/3jGnWGEJwnefnSzGJfOsn9/8ce6dd7468000b124b0e4c94d847a345/BYD_Atto_3_Evo-Eksteri%C3%83_r-5.jpg?fm=webp&w=640&q=75",
        title: "Test: BYD Atto 3 Evo burde være bedre efter stor opdatering",
        reviewUrl: "https://blog.bilbasen.dk/test-byd-atto-3-evo-burde-vaere-bedre",
        reviewDate: new Date("2026-05-24"),
      },
    },
    1391: {
      // Dolphin Surf
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/Sl5cHNP66fVv8OtcBRROA/37565a75151429c500198faed901f1cd/BYD_Dolphin_Surf-17.jpeg?fm=webp&w=640&q=60",
        title: "Test: BYD Dolphin Surf Active – for billig til at være god?",
        reviewUrl: "https://blog.bilbasen.dk/test-byd-dolphin-surf-active-for-billig-til-at-vaere-god",
        reviewDate: new Date("2026-03-10"),
      },
    },
    2003637: {
      // Sealion 7
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/f3385703-7643-322d-8bb8-1bf3a7961626/f3aa2cc7769826063bb84f51ab2d4bf4/TEST-BYD-Sealion-7-AWD-MBB-354.jpeg?w=640&fm=webp&q=75",
        title: "Test: BYD Sealion 7 er tung luksus",
        reviewUrl: "https://blog.bilbasen.dk/test-byd-sealion-7-er-tung-luksus-475887",
        reviewDate: new Date("2024-12-28"),
      },
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4xcMf556yXhE9e9Pjdhsn4/c08bc2512a0afa830a3cba5b2aeadf1d/BYD_Sealion_7-16.jpg?w=640&fm=webp&q=75",
        title: "BYD Sealion 7 - trivelig sjøløve",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/byd-sealion-7-trivelig-sjolove",
        reviewDate: new Date("2025-12-13"),
      },
    },
    2000655: {
      // Seal
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/86091b85-5c1e-3bfb-b5df-8fd3b00d850e/5095abae7b98607df11e0e49ad646166/TEST-BYD-Seal-RWD-MBB-315.jpeg?w=640&fm=webp&q=75",
        title: "Test: BYD Seal Design er en af de mest velkørende Kina-biler",
        reviewUrl: "https://blog.bilbasen.dk/test-byd-seal-design-en-af-de-mest-velkoerende-kina-biler-471768",
        reviewDate: new Date("2024-10-26"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/3BVG83zfBqIMeCSQckLD40/7babc7b0d89a0de9b38de854f9680b72/Ligegyldig_Lexus.jpeg?w=640&fm=webp&q=75",
        title: "Test: BYD Seal – kinesisk körglädje",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-byd-seal-kinesisk-koerglaedje",
        reviewDate: new Date("2025-01-30"),
      },
    },
    3481: {
      // Seal 6
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5gOqvbv30fmnDwF9JPyxdL/c4d3f88b2b7f7f8d09d1d5d6a228d984/BYD_Seal_6_Touring-1.jpg?w=640&fm=webp&q=75",
        title: "Test: BYD Seal 6 Touring – en ”sittuation” ",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-byd-seal-6-touring-en-sittuation",
        reviewDate: new Date("2026-05-21"),
      },
    },
    8368: {
      // Tang
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/30783687-5468-3987-be6a-8b88cfcdb3ec/8b8adc47466d74254710627bd1ae4561/BYD-Tang-Facelift-04.jpg?w=640&fm=webp&q=75",
        title: "Test: BYD Tang er bedre, billigere og stadig overraskende lækker",
        reviewUrl: "https://blog.bilbasen.dk/test-byd-tang-er-bedre-billigere-og-stadig-overraskende-laekker-474721",
        reviewDate: new Date("2024-12-01"),
      },
    },
  },
  749: {
    // BMW
    2466: {
      // X-Serie
      2000594: {
        // iX1
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/974572f6-d097-3df6-b33a-3f193b311b43/83c2cc52c649ac1ddb6a3e206f895d77/TEST-BMW-iX1-eDrive20-FC-MBB51.jpeg?w=640&fm=webp&q=75",
          title: "Test: BMW iX1 er en bil, man har på privatleasing",
          reviewUrl: "https://blog.bilbasen.dk/test-bmw-ix1-er-en-bil-man-har-paa-privatleasing-468206",
          reviewDate: new Date("2024-06-22"),
          specKeys: ["eDrive20"],
        },
      },
      1068: {
        // iX2
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/46452d15-1e1e-34ed-a584-ff7ee4562397/6395c6b8a565bb1cf139c280937005dd/BMW-iX2_-33.jpg?w=640&fm=webp&q=75",
          title: "Test: Den nye iX2 er en BMW ind til benet – men til hvem?",
          reviewUrl: "https://blog.bilbasen.dk/test-den-nye-ix2-er-en-bmw-ind-til-benet-men-til-hvem-466776",
          reviewDate: new Date("2024-05-22"),
        },
      },
      2000532: {
        // iX3
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/1EU0DohHuhiamB6p1Wuh75/f124b641e26ab12e819deb37d436526b/BMW-iX3-10.jpg?w=640&fm=webp&q=75",
          title: "Test: Nya BMW iX3 – årets mest imponerande och viktigaste bil",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-nya-bmw-ix3-arets-mest-imponerande-och-viktigaste-bil",
          reviewDate: new Date("2025-12-18"),
        },
      },
      6737: {
        // X5
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/5c696aa7-e140-3697-9616-defa2480b7f2/fc4b8e224f8f82e4e74183c77d33b617/MBB-TEST-BMW-X5-50e-179.jpeg?w=640&fm=webp&q=75",
          title: "Test: BMW X5 er stadig den bedste i klassen",
          reviewUrl: "https://blog.bilbasen.dk/test-bmw-x5-er-stadig-den-bedste-i-klassen-460113",
          reviewDate: new Date("2024-02-03"),
        },
      },
    },
    2132: {
      // 3-Serie
      893: {
        // M3
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/280954e9-d1ac-3342-9025-0a81b55a2186/258855b2ab91870244cc58bcfcd7fe69/BMW-M3-Touring-44.jpg?w=640&fm=webp&q=75",
          title: "Test: BMW M3 Touring er svaret på dine problemer",
          reviewUrl: "https://blog.bilbasen.dk/test-bmw-m3-touring-er-svaret-paa-dine-problemer-454079",
          reviewDate: new Date("2023-10-04"),
        },
      },
    },
    2131: {
      // 5-Serie
      2000633: {
        // i5
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/6e720e4d-f8f0-3bbc-a697-5f8384396de3/72246621368eb7c30595357fe5e2a893/TEST-BMW-i5-eDrive40-16.jpeg?w=640&fm=webp&q=75",
          title: "Test: Køreglæden er stor (men også kortvarig) i BMW i5 eDrive40",
          reviewUrl: "https://blog.bilbasen.dk/test-koereglaeden-er-stor-men-ogsaa-kortvarig-i-bmw-i5-edrive40-460058",
          reviewDate: new Date("2024-01-20"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/4EndVzvMTOfX9v7055fqLk/868332e6fcb24c421ec1b110cf786078/BMW_i5_Touring__1_.jpg?w=640&fm=webp&q=75",
          title: "Test: Mer plats och smartare design i BMW i5 Touring",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-mer-plats-och-smartare-design-i-bmw-i5-touring",
          reviewDate: new Date("2025-01-30"),
        },
      },
    },
    2130: {
      // 7-Serie
      2000595: {
        // i7
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/84648da7-7b8f-3416-b3ac-fcaa0858284c/f8185f74bc335b7cb83a8c7b7a6a41ee/TEST-BMW-i7-M70-3306.jpeg?w=640&fm=webp&q=75",
          title: "Test: Med M for Magt i BMW i7 M70",
          reviewUrl: "https://blog.bilbasen.dk/test-med-m-for-magt-i-bmw-i7-m70-466949",
          reviewDate: new Date("2024-06-08"),
        },
      },
    },
  },
  757: {
    // Citroen
    2518: {
      // C5-Serie
      2367: {
        // E-C5 Aircross
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/1KIPfCCpZupX1afQfjJ11o/7961a22761c5c8ab459099ace8b5ed50/Citroe%C3%8C_n_E%C3%8C_-C5_Aircross_-_Eksteri%C3%83_r-05.jpg?fm=webp&w=640&q=60",
          title: "Test: Citroën Ë-C5 Aircross – fransk komfortkonge til under 300.000 kr.",
          reviewUrl: "https://blog.bilbasen.dk/test-citroen-e-c5-aircross-fransk-komfortkonge",
          reviewDate: new Date("2026-03-01"),
        },
      },
    },
  },
  8106: {
    // Cupra
    2000567: {
      // Born
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/2DFL5WNf0L3OLVGLsQXS9c/ae5cc48e29b5438369589e98e89b22a6/Cupra_Born_2026_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Born - Teller på knappene",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-cupra-born-teller-pa-knappene",
        reviewDate: new Date("2026-05-08"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/2DFL5WNf0L3OLVGLsQXS9c/ae5cc48e29b5438369589e98e89b22a6/Cupra_Born_2026_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Born – knapphändig",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-cupra-born-knapphandig",
        reviewDate: new Date("2026-05-07"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/6fp2GNWySsXnS8RGLgm6mW/330dd465ecd51f8652b5a595a6644417/Cupra_Born_2026_test-1.jpg?fm=webp&w=640&q=60",
        title: "Første tur: Opdateret Cupra Born har fået flere knapper – men stadig ikke nok",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-opdateret-cupra-born-har-faet-flere-knapper",
        reviewDate: new Date("2026-05-18"),
      },
    },
    2001675: {
      // Tavascan
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/26cdd4e3-fbfd-3616-84ec-cf7f33ca19a6/643a3a9593be761bf04779eda7b98e16/Cupra-Tavascan-VZ-27.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Tavascan VZ er sjovere end sine søskende",
        reviewUrl: "https://blog.bilbasen.dk/test-cupra-tavascan-vz-er-den-sjoveste-af-sine-soeskende-472355",
        reviewDate: new Date("2024-10-06"),
      },
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5Jq8fAIQgqJexjI3UGHecA/0052ce07ef32d941b02df8098bd6dc40/Cupra_Tavascan_VZ-46.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Tavascan VZ – Spansk fiesta med tysk innmat",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-cupra-tavascan-vz-spansk-fiesta-med-tysk-innmat",
        reviewDate: new Date("2026-03-21"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/7grr1uJEN24yrXWkkg5mM/669eca3c5465a0c8158f4dfc8df6da86/Cupra_Tavascan__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Tavascan VZ – roligare än sina syskon",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-cupra-tavascan-vz-roligare-aen-sina-syskon",
        reviewDate: new Date("2025-01-30"),
      },
    },
    598: {
      // Terramar
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/2oCkpHkREEXvUQRbAsTbqv/bbd386aac490a315803993e86e29bf57/Cupra_Terramar_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Terramar – form & funktion,",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-cupra-terramar-form-and-funktion",
        reviewDate: new Date("2026-05-22"),
      },
    },
    8305: {
      // Formentor
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/49fa06da-bf3d-33d6-a7fc-714a7e692429/68a7b11e494516cadc3650eb0f62fa47/Cupra-Formentor-38.jpg?w=640&fm=webp&q=75",
        title: "Test: Opdateret Cupra Formentor viser et aktivt valg",
        reviewUrl: "https://blog.bilbasen.dk/test-cupra-formentor-2024-472248",
        reviewDate: new Date("2024-10-05"),
      },
    },
    3605: {
      // Raval
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/20a6CUAg6Gt0RpYvJS0n44/9218ea8b7ecc6dd7bddcccf201f69c07/Cupra_Raval_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Cupra Raval – Stor & Liten",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-cupra-raval-stor-och-liten",
        reviewDate: new Date("2026-04-10"),
      },
    },
  },
  1287: {
    // Exlantix
    1289: {
      // ES
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/1S4oAzihoKiezPNGh7BxUG/f930141e4030987bd4b7e31f6258fbd9/F%C3%83_rste_tur_Exlantix_ES_482.jpeg?fm=webp&w=640&q=60",
        title: "Test: Sedanen ES er en overbevisende start fra Exlantix",
        reviewUrl: "https://blog.bilbasen.dk/test-sedanen-es-er-en-overbevisende-start-fra-exlantix",
        reviewDate: new Date("2026-04-18"),
      },
    },
  },
  766: {
    // Fiat
    2662: {
      // Panda-Serie
      2167: {
        // Grande Panda E
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/4JZYS9uSZqhuOk4czDjvws/6bcd85477ae8b852c0f70e71c97379b4/Fiat_Grande_Panda-18.jpg?w=640&fm=webp&q=75",
          title: "Fiat Grande Panda - veldig rimelig, eller litt billig?",
          reviewUrl:
            "https://www.finn.no/bilguiden/tester-og-anmeldelser/fiat-grande-panda-veldig-rimelig-eller-litt-billig",
          reviewDate: new Date("2025-12-08"),
        },
      },
    },
  },
  2172: {
    // Firefly
    2173: {
      // Firefly
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/7ml4qvJvA2nsHsgzNysnf/af49edf47807c85b636787c0c3980da1/firefly_cover.jpg?fm=webp&w=640&q=60",
        title: "Test: På langtur i vinterkulde med Firefly",
        reviewUrl: "https://blog.bilbasen.dk/test-pa-langtur-i-vinterkulde-med-firefly",
        reviewDate: new Date("2026-02-01"),
      },
    },
  },
  767: {
    // Ford
    2001856: {
      // Capri
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/94e13e6f-bbf4-391d-a8fa-eecf411b6a46/7078e416d69c8d8e73d9bffde25bfdd2/Ford-Capri-21.jpg?w=640&fm=webp&q=75",
        title: "Test: Ford Capri er et mærkeligt mix, der godt kan fungere",
        reviewUrl: "https://blog.bilbasen.dk/test-ford-capri-er-et-maerkeligt-mix-der-godt-kan-fungere-478281",
        reviewDate: new Date("2025-03-02"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/49SiSY8Znz7qi5EzBMhW0E/34881d158cf095b0e291e37472ad3f40/Ford_Capri__1_.jpeg?w=640&fm=webp&q=75",
        title: "Första turen: Ford Capri kör bredvid minnenas allé",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/foersta-turen-ford-capri-koer-bredvid-minnenas-alle",
        reviewDate: new Date("2025-01-29"),
      },
    },
    2848: {
      // Explorer
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/4f0ffb68-84ae-3fbf-95e4-660c6408793d/5d4c32aab4308c3955f5e29eea87c270/TEST-Ford-Explorer-MBB-6079.jpeg?w=640&fm=webp&q=75",
        title: "Test: Ford Explorer gør det godt med hjælp fra VW",
        reviewUrl: "https://blog.bilbasen.dk/test-ford-explorer-goer-det-godt-med-hjaelp-fra-vw-471603",
        reviewDate: new Date("2024-09-14"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1kVPZVWGaKeHy9AWGCf3vT/4dd47a7579983747ae3552dc85f6fe26/Ford_Explorer__1_.jpeg?w=640&fm=webp&q=75",
        title: "Test: Ford Explorer gör det bra med hjälp från VW",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-ford-explorer-goer-det-bra-med-hjaelp-fran-vw",
        reviewDate: new Date("2025-01-30"),
      },
    },
    2854: {
      // Ranger
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4UGZsW9FqUCEVLRzb6g0Nh/0db50085e3a5e674570537b3ead5f240/Ford_Ranger_MS-RT_test-5.jpg?w=640&fm=webp&q=75",
        title: "Test: Ford Ranger MS-RT – Asfaltcowboyens jernhest",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-ford-ranger-ms-rt-asfaltcowboyens-jernhest",
        reviewDate: new Date("2026-05-27"),
      },
    },
  },
  772: {
    // Hyundai
    2000609: {
      // Ioniq 6
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/6JgL672bTmv7ZPOZzU23g5/42016e90eb96908246249796c19ed6bd/Hyundai_Ioniq_6-27.jpg?fm=webp&w=640&q=60",
        title: "Test: Hyundai Ioniq 6 er bedre end nogensinde – men spar på udstyret",
        reviewUrl: "https://blog.bilbasen.dk/test-hyundai-ioniq-6-er-bedre-end-nogensinde",
        reviewDate: new Date("2026-04-15"),
      },
    },
    3646: {
      // Ioniq 6N
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/bBPdBEbMqyYnhuhgCTfDo/00e72047954361906d5d09fa786c7b86/Hyundai_Ioniq_6_N-1-kopi.jpg?w=640&fm=webp&q=75",
        title: "Første tur: Hyundai Ioniq 6 N leverer 650 hk – digitale tryllekunster gir ekte kjøreglede",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-hyundai-ioniq-6-digital-kjoreglede",
        reviewDate: new Date("2026-03-29"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/bBPdBEbMqyYnhuhgCTfDo/00e72047954361906d5d09fa786c7b86/Hyundai_Ioniq_6_N-1-kopi.jpg?w=640&fm=webp&q=75",
        title: "Test: Hyundai Ioniq 6 N– bästa illusionen",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-hyundai-ioniq-6-n-basta-illusionen",
        reviewDate: new Date("2026-03-24"),
      },
    },
    2000546: {
      // Ioniq 5
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/b6817c8b-2d18-3c2a-a754-0e0feb9bef7e/3d2eafb11e773304a149a76005d7e369/Hyundai-Ioniq-5-Facelift-14.jpg?w=640&fm=webp&q=75",
        title: "Test: Faceliftet Hyundai Ioniq 5 fortjener en chance til",
        reviewUrl: "https://blog.bilbasen.dk/test-faceliftet-hyundai-ioniq-5-fortjener-en-chance-til-474130",
        reviewDate: new Date("2024-11-17"),
      },
    },
    6739: {
      // Santa Fe
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/d38f669f-be23-3fea-a4b0-4b5a11c1d0c5/91a85cd1988c521c4ecf124c14c39eb8/Hyundai-Santa-Fe-32.jpg?w=640&fm=webp&q=75",
        title: "Test: Hyundai Santa Fe – strømført koreansk kampvogn med 7 sæder",
        reviewUrl: "https://blog.bilbasen.dk/test-hyundai-santa-fe-stroemfoert-koreansk-kampvogn-med-7-saeder-479585",
        reviewDate: new Date("2025-04-06"),
      },
    },
    2000438: {
      // Kona
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/9acb40dd-d97f-3ea3-8a84-7f6029585b6b/f9881980b5c64680cb137ba0a25f1ef9/TEST-Hyundai-Kona-16-T-GDI-N-Line-3061.jpeg?w=640&fm=webp&q=75",
        title: "Test: Hyundai Kona smider fornuften overbord med 198 hk",
        reviewUrl: "https://blog.bilbasen.dk/test-hyundai-kona-smider-fornuften-overbord-med-198-hk-456204",
        reviewDate: new Date("2023-12-02"),
      },
    },
    2000069: {
      // i30
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/f5fcc500-1afd-327b-b6bb-c0a03346f1cd/b59adbe852c84452c05110852357bc5f/Hyundai-i30-stationcar-24.jpg?w=640&fm=webp&q=75",
        title: "Test: Hyundai i30 stationcar gør det godt på langtur",
        reviewUrl: "https://blog.bilbasen.dk/test-hyundai-i30-stationcar-2024-469831",
        reviewDate: new Date("2024-08-04"),
      },
    },
    7951: {
      // Tucson
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/ZjAppKLUHTXPG7UWBJl7s/1d4be1c7ded9c1872349073339cee16c/test-Hyundai_Tucson_HEV-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Hyundai Tucson Hybrid – fotriktig folkfraktare",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-hyundai-tucson-hybrid-fotriktig-folkfraktare",
        reviewDate: new Date("2026-03-02"),
        fuelType: FuelType.HybridPetrol,
      },
    },
  },
  3171: {
    // Jaecoo
    3172: {
      // J5
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/7bGRIECsRn8dGWZXwJlYtv/f4a17a7f31c3b9bf86818e42495ada59/F%C3%83_rste_tur_Jaecoo_J5_EV_MBB_098.jpeg?fm=webp&w=640&q=60",
        title: "Første tur: Jaecoo J5 EV er solid elektrisk basiskost",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-jaecoo-j5-ev-er-solid-elektrisk-basiskost",
        reviewDate: new Date("2026-02-06"),
      },
    },
  },
  777: {
    // Kia
    2677: {
      // Ceed-Serie
      2000064: {
        // Ceed
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/fe6fed29-d47f-31ca-b382-e72d983270ad/6e2d3d243ae5e1ed3420229ca30396a1/TEST-Kia-Ceed-SW-15-2335.jpeg?w=640&fm=webp&q=75",
          title: "Test: Kia Ceed er populær, og det er velfortjent",
          reviewUrl: "https://blog.bilbasen.dk/test-kia-ceed-er-populaer-og-det-er-velfortjent-465138",
          reviewDate: new Date("2024-04-24"),
        },
      },
    },
    3554: {
      // EV2
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1gqPj83H0LXyHwTmKH9Gy4/8bce9bf0b42c5475c3188c1c9a481812/kia-ev2-base-line-frost-blue-dynamic-digital-1920x1080-002.jpg?w=640&fm=webp&q=75",
        title: "Test: EV2 konkurrerer i en beinhard klasse med én klar fordel",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-ev2-konkurrerer-i-en-beinhard-klasse-med-en-klar-fordel",
        reviewDate: new Date("2026-03-31"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4jUbqPqcEhzmRtvin2K8VZ/62d83a2d45d360546be0ebd9eeba61a9/Kia_EV2_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Kia EV2 – med en tydlig fördel",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-kia-ev2-med-en-tydlig-fordel",
        reviewDate: new Date("2026-03-31"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/2mKVb9iQ5g7Wb7NJjfnZj3/13f199c920e3d40f8d9d443411417651/kia-ev2-base-line-frost-blue-dynamic-digital-1920x1080-004.jpeg?fm=webp&w=640&q=60",
        title: "Første tur: Kia EV2 rammer en benhård klasse med en klar fordel",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-kia-ev2-rammer-en-benhard-klasse",
        reviewDate: new Date("2026-03-30"),
      },
    },
    552: {
      // EV3
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/8bc64b89-1ff0-3fcd-9079-eabffc3ad684/abc43b86b41806bc9de0fa2fe92bc820/TEST-Kia-EV3-SR-MBB-35.jpeg?w=640&fm=webp&q=75",
        title: "Test: Selv den billigste Kia EV3 til 269.999 kr. er en fremragende elbil",
        reviewUrl:
          "https://blog.bilbasen.dk/test-selv-den-billigste-kia-ev3-til-269-999-kr-er-en-fremragende-elbil-476080",
        reviewDate: new Date("2025-01-11"),
      },
    },
    2161: {
      // EV4
      bilbasen: [
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/1wxKwTDIlpVyuUDzEjWCKw/639b20f31c2dc7fdb6331586f6618031/TEST_Kia_EV4_LR_Access_MBB_871.jpeg?w=640&fm=webp&q=75",
          title: "Test: Kia EV4 er god til det meste - og bedre end EV3",
          reviewUrl: "https://blog.bilbasen.dk/test-kia-ev4-er-god-til-det-meste-og-bedre-end-ev3",
          reviewDate: new Date("2025-11-08"),
        },
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/2HeTTaJcN7pZyeeywQfl7a/dd235a86fb26c9e34b73851b978a950e/Kia_EV4_Fastback_-_Eksteri%C3%83_r-16.jpg?fm=webp&w=640&q=60",
          title: "Test: Kia EV4 Fastback – hvis bare den ikke var grim",
          reviewUrl: "https://blog.bilbasen.dk/test-kia-ev4-fastback-hvis-bare-den-ikke-var-grim",
          reviewDate: new Date("2026-04-05"),
        },
      ],
    },
    2299: {
      // EV5
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4UsOHUY1k6gT5jPrUlxzwk/409ef4e39c272796262ded7e1035dfab/Kia_EV5_test-5.jpg?w=640&fm=webp&q=75",
        title: "Test: Kia EV5 - knalltøff familie-SUV",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-kia-ev5-knalltoff-familie-suv",
        reviewDate: new Date("2026-04-21"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/6pFy88NUFUZ2322jCEruzF/d059ef39d951610360ae108a6c32e197/F%C3%83_RSTE_TUR_Kia_EV5_MBB_663.jpeg?fm=webp&w=640&q=60",
        title: "Første tur: Kia EV5 er klar til det gennemsnitlige familieliv",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-kia-ev5-er-klar-til-det-gennemsnitlige-familieliv",
        reviewDate: new Date("2026-03-06"),
      },
    },

    8300: {
      // EV6
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/7mE1v7vXk0FmmvgaZjlnTc/7850d0d2e7641e6ae2f6b3b05f866cb7/Kia_EV6_GT.jpg?w=640&fm=webp&q=75",
        title: "Kia EV6 GT - Rally snøfreser",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/kia-ev6-gt-rally-snofreser",
        reviewDate: new Date("2026-01-25"),
      },
    },
    2000638: {
      // EV9
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/4mXw0GLXdXDdlCiNeWZLLf/4ca3b03bbeac9aa287cb3af157d9bfaf/TEST_Kia_EV9_GT_MBB_570.jpeg?fm=webp&w=640&q=60",
        title: "Test: Kia EV9 GT er til de største fans",
        reviewUrl: "https://blog.bilbasen.dk/test-kia-ev9-gt-er-til-de-storste-fans",
        reviewDate: new Date("2026-01-17"),
      },
    },
    8178: {
      // Picanto
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/a42253a0-092b-37dc-bfdc-92107c1e4819/f8afdd484654e658f022cc71f76eca83/Kia-Picanto-Facelift-26.jpg?w=640&fm=webp&q=75",
        title: "Test: Faceliftet og fiks Kia Picanto mangler kræfter",
        reviewUrl: "https://blog.bilbasen.dk/test-faceliftet-og-fiks-kia-picanto-mangler-kraefter-473390",
        reviewDate: new Date("2024-10-27"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1cVAyD2yy3ccBv77nIIpwe/3b70f327d817e752639c55edc3255fe6/Kia_Picanto__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Uppdaterade Kia Picanto saknar kraft",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-uppdaterade-kia-picanto-saknar-kraft",
        reviewDate: new Date("2025-01-29"),
      },
    },
    1658: {
      // PV5
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/227ctShzdTuhhyS06zDWsr/31f33a8bb017734987b114b4ef3f41ab/TEST_Kia_PV5_Passenger_LR_MBB_136.jpeg?fm=webp&w=640&q=60",
        title: "Test: Kia PV5 Passenger er et fund for alle med store pladsbehov",
        reviewUrl: "https://blog.bilbasen.dk/test-kia-pv5-passenger-er-et-fund-for-alle-med-store-pladsbehov",
        reviewDate: new Date("2026-01-02"),
      },
    },
  },
  2176: {
    // Leapmotor
    2177: {
      // B10
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/pKvvV6rkKwOxZWiQODAg6/bbf85b1a817da3f6f870a81e78354fe6/Leapmotor_B10-34.jpg?fm=webp&w=640&q=60",
        title: "Test: Leapmotor B10 er en budgetbil, der overrasker",
        reviewUrl: "https://blog.bilbasen.dk/test-leapmotor-b10-er-en-budgetbil-der-overrasker",
        reviewDate: new Date("2026-02-11"),
      },
    },
  },
  782: {
    // Lexus
    2002289: {
      // LBX
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/3bc5898b-448d-3f14-89ce-4f0e65b63073/2de7e9f33be7e9f0902cff63db5b60a0/TEST-Lexus-LBX-MBB-5356.jpeg?w=640&fm=webp&q=75",
        title: "Test: Liten, lækker, luksuriøs Lexus",
        reviewUrl: "https://blog.bilbasen.dk/lille-laekker-og-luksurioes-lexus-470332",
        reviewDate: new Date("2024-08-24"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/43YzTutMPktCNmlea8ytf2/d436c632485583be2a2444b37652141b/Lexus_LBX__1_.jpeg?w=640&fm=webp&q=75",
        title: "Test: Liten, lyxig och elegant Lexus",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-liten-lyxig-och-elegant-lexus",
        reviewDate: new Date("2025-01-31"),
      },
    },
  },
  784: {
    // Mazda
    8063: {
      // 2
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/7lxJIxqKIPZ6VR4hnxu0nT/c1bc22e2a8f1b497e4bed4e88d966c0d/Mazda_2_Hybrid__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Prispressad Toyota-teknik i uppdaterade Mazda 2 Hybrid",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-prispressad-toyota-teknik-i-uppdaterade-mazda-2-hybrid",
        reviewDate: new Date("2025-01-31"),
      },
    },
    1188: {
      // CX-80
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/7ed491af-4ce1-386f-935f-3aabf1018b03/f8083375f1104ca8ded078f70dd74756/Mazda-CX-80-14.jpg?w=640&fm=webp&q=75",
        title: "Test: Mazda CX-80 giver gammeldags luksus og 7 sæder",
        reviewUrl: "https://blog.bilbasen.dk/test-mazda-cx-80-gammeldags-luksus-og-7-saeder-478901",
        reviewDate: new Date("2025-03-23"),
      },
    },
    1155: {
      // MX-5
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/bbd180d7-ae55-32db-b89f-cd0e1ec211a9/9f45f5a3a34e9440fa187fd254f52f3b/Mazda-MX-5-2024-Eksterioer-02.jpg?w=640&fm=webp&q=75",
        title: "Test: Mazda gør det svært for sig selv med ny opdatering af MX-5",
        reviewUrl: "https://blog.bilbasen.dk/test-mazda-goer-det-svaert-for-sig-selv-med-ny-opdatering-af-mx-5-470719",
        reviewDate: new Date("2024-08-28"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5C5VQ14b13n5m6apQWbUGu/ac77f92d25527104f9d5c277a7dea191/Mazda_MX-5__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Mazda gør det svårt för sig selv med ny opdatering af MX-5",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-mazda-goer-det-svart-foer-sig-med-ny-uppdatering-av-mx-5",
        reviewDate: new Date("2025-01-31"),
      },
    },
  },
  785: {
    // Mercedes-Benz
    3776: {
      // C-Class
      2002576: {
        // C63
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/5wpD5HUyoLgpIFUc0eMWU3/17fd44b6a75ed2cb7d39d6022e269f03/Mercedes-AMG_C_63_S__1_.jpg?w=640&fm=webp&q=75",
          title: "Test: Mercedes-AMG C 63 S E Performance är svår att tycka om",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-mercedes-amg-c-63-s-e-performance-aer-svar-att-tycka-om",
          reviewDate: new Date("2025-01-17"),
        },
      },
    },
    2000250: {
      // CLA-Class
      2002592: {
        // CLA200
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/3fnUac0s2OZWHTH9mCCGbK/15b68eeca1a2e8b713ed1d15c4cac229/Mercedes-Benz_CLA_200_-_Eksteri%C3%83_r-6.jpg?fm=webp&w=640&q=75",
          title: "Test: Kan man nøjes med den billigste Mercedes-Benz CLA 200?",
          reviewUrl: "https://blog.bilbasen.dk/test-kan-man-nojes-med-den-billigste-mercedes-cla",
          reviewDate: new Date("2026-06-07"),
        },
      },
      1371: {
        // CLA250+
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/10Dmw1X8ApEKnXnWmleWGz/1fb77b4438ff4f977d1ff5182c2c2366/TEST_Mercedes-Benz_CLA_250__Shooting_Brake_MBB_577.jpeg?fm=webp&w=640&q=60",
          title: "Test: Mercedes-Benz CLA Shooting Brake ofrer sig for skønheden og det lave forbrug",
          reviewUrl:
            "https://blog.bilbasen.dk/test-mercedes-benz-cla-shooting-brake-ofrer-sig-for-skonheden-og-det-lave-forbrug",
          reviewDate: new Date("2026-06-05"),
          specKeys: ["Shooting Brake"],
        },
      },
      1374: {
        // CLA350
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/BzZRRPbVpbUmXS5D1wXYn/3ae97a12a8b18fde0c21f0467377eae0/Mercedes_CLA-12.jpg?w=640&fm=webp&q=75",
          title: "Mercedes-Benz CLA - Årets bil i Europa",
          reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/mercedes-benz-cla-arets-bil-i-europa",
          reviewDate: new Date("2026-02-28"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/30XCNxuB6R9g0xw8C54IP3/61f4cbaaaae97ff2e76910c7a9716185/Mercedes-Benz_CLA_Shooting_Brake-27.jpg?w=640&fm=webp&q=75",
          title: "Test: Mercedes CLA Shooting Brake – räckviddsstjärnan",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-mercedes-cla-shooting-brake-rackviddsstjarnan",
          reviewDate: new Date("2026-04-23"),
        },
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/3erqRkYLc0HzomHQJKh31e/c3a21cfb61763e2c0c670fb08259ae15/TEST_MB_CLA_350_4Matic_MBB_854.jpeg?fm=webp&w=640&q=60",
          title: "Test: Mercedes-Benz CLA 350 4Matic er ubesværet i ordets bedste forstand",
          reviewUrl:
            "https://blog.bilbasen.dk/test-mercedes-benz-cla-350-4matic-er-ubesvaeret-i-ordets-bedste-forstand",
          reviewDate: new Date("2026-01-31"),
        },
      },
    },
    1507: {
      // G-Class
      2002728: {
        // G450 d
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/3KE0ktdTddCT4KNbgCjlGV/112385110801c7fec816f2c011c0299f/Mercedes-Benz_G_450_D-3.jpg?w=640&fm=webp&q=75",
          title: "Mercedes-Benz G 450 d - Dieseldrevet spa",
          reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/mercedes-benz-g-450-d-dieseldrevet-spa",
          reviewDate: new Date("2026-01-31"),
        },
      },
      2002731: {
        // G580
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/6beeab13-fda3-309b-93d3-53f0eacabdfc/31d693c2ac8f165f703117bc4a1b7385/TEST-MB-G-580-EQ-262.jpeg?w=640&fm=webp&q=75",
          title: "Test: Mercedes-Benz G 580 med EQ Technology – en absurd elbil",
          reviewUrl: "https://blog.bilbasen.dk/test-mercedes-benz-g-580-med-eq-technology-en-absurd-elbil-478695",
          reviewDate: new Date("2025-04-25"),
        },
      },
    },
    2000536: {
      // EQA-Class
      2002679: {
        // EQA250+
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/48654677-c38b-33cd-b224-2e35e5dd9958/25f6bb0333eec15cdd5617add61292ca/TEST-MB-EQA-250-FL-MBB-3237.jpeg?w=640&fm=webp&q=75",
          title: "Test: Fornyelse til Mercedes EQA, men perfektionen udebliver",
          reviewUrl: "https://blog.bilbasen.dk/test-fornyelse-til-mercedes-eqa-men-perfektionen-udebliver-466424",
          reviewDate: new Date("2024-05-11"),
        },
      },
    },
    2000559: {
      // EQB-Class
      2002685: {
        // EQB350
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/5d5a435b-0877-33d3-8008-083424dff43d/afa802987fc744f4cd4de2989f6969b4/Mercedes-Benz-EQB-27.jpg?w=640&fm=webp&q=75",
          title: "Test: Opdateret Mercedes EQB står (næsten) alene som elbil med syv sæder",
          reviewUrl: "https://blog.bilbasen.dk/test-opdateret-mercedes-eqb-466253",
          reviewDate: new Date("2024-05-12"),
        },
      },
    },
    8393: {
      // GLB-Class
      2002768: {
        // GLB220 d
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/0ca60e3e-1d74-3c57-a683-ff5089384a36/6c962b7a23d14e8b27e1fbb2ff67d7d9/Mercedes-Benz-GLB-220d-17.jpg?w=640&fm=webp&q=75",
          title: "Test: Opdateret Mercedes-Benz GLB med plads til både børn og betjente",
          reviewUrl:
            "https://blog.bilbasen.dk/test-opdateret-mercedes-benz-glb-med-plads-til-baade-boern-og-betjente-465973",
          reviewDate: new Date("2024-05-05"),
        },
      },
      1630: {
        // GLB250+
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/6iikho0W9xXtQAoIbdoo9r/2892751972a5b9ef3f4914be57b9072c/MB_GLB_250__MBB_805.jpeg?fm=webp&w=640&q=60",
          title: "Test: Ny elektrisk Mercedes-Benz GLB er tæt på at være den fuldendte familiebil",
          reviewUrl:
            "https://blog.bilbasen.dk/test-ny-elektrisk-mercedes-benz-glb-er-taet-pa-at-vaere-den-fuldendte-familiebil",
          reviewDate: new Date("2026-04-25"),
        },
      },
      1631: {
        // GLB350
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/6fD3fuGFyNWtvJl0K4cK6R/193fac5e572e0ae40e62cb539400026a/Mercedes-Benz_GLB-6_kopi.jpg?w=640&fm=webp&q=75",
          title: "Test: Mercedes-Benz GLB – fleksibel luksus",
          reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/mercedes-benz-glb-fleksibel-luksus",
          reviewDate: new Date("2026-04-10"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/6fD3fuGFyNWtvJl0K4cK6R/193fac5e572e0ae40e62cb539400026a/Mercedes-Benz_GLB-6_kopi.jpg?w=640&fm=webp&q=75",
          title: "Test: Mercedes GLB – flexibel luksus",
          reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-mercedes-glb-flexibel-lyx",
          reviewDate: new Date("2026-04-15"),
        },
      },
    },
    2000327: {
      // GLE-Class
      2002796: {
        // GLE450 d
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/a6c9083a-3a63-3145-9488-b8876bc7d483/4bfa7edd2bba018c0bf44a848a71ba7d/Mercedes-Benz-GLE-450d-14.jpg?w=640&fm=webp&q=75",
          title: "Test: Mercedes-Benz GLE er en drøm som seksbenet diesel-dinosaur",
          reviewUrl: "https://blog.bilbasen.dk/test-mercedes-benz-gle-er-en-droem-som-seksbenet-diesel-dinosaur-461119",
          reviewDate: new Date("2024-02-05"),
        },
      },
    },
    3775: {
      // E-Class
      2002646: {
        // E220 d
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/315bd740-b985-34fd-953e-e9a4a7df9b1b/05dbb5de2e8f74fdfae3e3685d7e6f2d/Mercedes-Benz-E-220-d-31.jpg?w=640&fm=webp&q=75",
          title: "Test: Mercedes-Benz E 220 d beviser dieselmotorens berettigelse",
          reviewUrl: "https://blog.bilbasen.dk/test-mercedes-benz-e-220-d-beviser-dieselmotorens-berettigelse-467011",
          reviewDate: new Date("2024-05-26"),
        },
      },
    },
  },
  786: {
    // MG
    688: {
      // 4
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5JI8ehrArXBelGyIIQVpKj/b5aadeb127fdbbdbc097479e34c86dc8/F%C3%83_rste_tur_MG4_Urban5174.jpeg?fm=webp&w=640&q=60",
        title: "Første tur: Kan MG4 EV Urban blive billig nok?",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-kan-mg4-ev-urban-blive-billig-nok",
        reviewDate: new Date("2026-03-26"),
      },
    },
    8399: {
      // MG4
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/rtCl4kIP1nGoeJMAe8KUl/14951741a18baeca31d7cdf7aa4c9747/MG4__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Välkörande MG4 kan inte längre konkurrera",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-vaelkoerande-mg4-kan-inte-laengre-konkurrera",
        reviewDate: new Date("2025-01-17"),
      },
    },
    2332: {
      // S6
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/2YyTJmP9idVOHY1mq80RKK/a2ff24cd0e6d24a3c97fd1aa111bf1e5/MGS6_Long_Range-11.jpg?fm=webp&w=640&q=60",
        title: "Test: MGS6 er den bedste MG nogensinde",
        reviewUrl: "https://blog.bilbasen.dk/test-mgs6-er-den-bedste-mg-nogensinde",
        reviewDate: new Date("2026-02-22"),
      },
    },
  },
  7147: {
    // MINI
    2697: {
      // Mini-Serie
      2000619: {
        // Cooper SE
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/7cb08445-2f2b-34ac-bf1c-2a48656c5695/62f8df42efd23e8e453fa2d637064300/TEST-Mini-Cooper-SE-MBB-442.jpeg?w=640&fm=webp&q=75",
          title: "Test: Maksimeret køreglæde i elektrisk Mini Cooper SE",
          reviewUrl: "https://blog.bilbasen.dk/test-maksimeret-koereglaede-i-elektrisk-mini-cooper-se-473086",
          reviewDate: new Date("2024-11-02"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/5rD07N6LuTSM1WdyWqZSRP/0539bf828b6326c44335c62dec36668e/Mini_Cooper_SE__1_.jpeg?w=640&fm=webp&q=75",
          title: "Maximerad körglädje i elektriska Mini Cooper SE",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/maximerad-koerglaedje-i-elektriska-mini-cooper-se",
          reviewDate: new Date("2025-01-29"),
        },
      },
    },
  },
  8109: {
    // NIO
    2736: {
      // ET5-Serie
      2000616: {
        // ET5
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/55NxFvrk6QiJqt0II77qJe/057b1618ae0b3e1f0ca027c5ef9654a3/Nio_ET5_Touring-02.jpeg?fm=webp&w=640&q=60",
          title: "Test: Skyder velbygget Nio ET5 Touring stadig med skarpt?",
          reviewUrl: "https://blog.bilbasen.dk/test-skyder-velbygget-nio-et5-touring-stadig-med-skarpt",
          reviewDate: new Date("2026-01-19"),
        },
      },
    },
  },
  801: {
    // Porsche
    2766: {
      // Cayenne-Serie
      2000612: {
        // Cayenne Coupé
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/7HkdZkmOr1syeq9VRZnO65/5c582eba1ea620abd0e3bb507550651f/332578_1920x1281.jpg?w=640&fm=webp&q=75",
          title: "Test: Porsche Cayenne Turbo Coupé Electric – Raffinert lynnedslag",
          reviewUrl:
            "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-porsche-cayenne-turbo-coupe-electric-raffinert-lynnedslag",
          reviewDate: new Date("2026-05-29"),
          specKeys: ["Turbo"],
        },
      },
      2003285: {
        // Cayenne Turbo
        finn: [
          {
            thumbnailUrl:
              "https://images.ctfassets.net/3xdpcmkzm6t5/DM0C9QUfl4vnunyuIiFuT/72ee0fd8a39af3c4af25d05e54844bec/327511_1920x1281.jpg?w=640&fm=webp&q=75",
            title: "Første tur: Porsche Cayenne Electric – familie-SUV med 1.156 hk og 3 500 kg på kroken",
            reviewUrl:
              "https://www.finn.no/bilguiden/tester-og-anmeldelser/forste-tur-porsche-cayenne-electric-familie-suv-med-1-156-hk-og-3-500-kg-pa",
            reviewDate: new Date("2026-03-27"),
            fuelType: FuelType.Electric,
          },
          {
            thumbnailUrl:
              "https://images.ctfassets.net/3xdpcmkzm6t5/7HkdZkmOr1syeq9VRZnO65/5c582eba1ea620abd0e3bb507550651f/332578_1920x1281.jpg?w=640&fm=webp&q=75",
            title: "Test: Porsche Cayenne Turbo Coupé Electric – Raffinert lynnedslag",
            reviewUrl:
              "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-porsche-cayenne-turbo-coupe-electric-raffinert-lynnedslag",
            reviewDate: new Date("2026-05-29"),
            specKeys: ["Coupé"],
          },
        ],
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/0o732oQDzFFX0FIIsXuGQ/3b870261e5f5db2d4dfa211563254a74/Porsche_Cayenne_Turbo_Electric_test-1_.jpg?w=640&fm=webp&q=75",
          title: "Test: Porsche Cayenne Electric – för fartfylld familj",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-porsche-cayenne-electric-for-fartfylld-familj",
          reviewDate: new Date("2026-04-09"),
          fuelType: FuelType.Electric,
        },
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/LKEZRCh79xjKf1LA0tSrd/b1f40f121e188733d9241fecd7cc9ba9/331384Porsche_Cayenne_Electric.jpg?fm=webp&w=640&q=60",
          title: "Første tur: Porsche Cayenne Electric – familie-SUV med 1.156 hk og 3.500 kg på krogen",
          reviewUrl: "https://blog.bilbasen.dk/forste-tur-porsche-cayenne-electric",
          reviewDate: new Date("2026-03-27"),
          fuelType: FuelType.Electric,
        },
      },
    },
    2764: {
      // Macan-Serie
      2003296: {
        // Macan GTS
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/5PJoH0yTDPXD9h8GMke7SX/26e53b8be892699a46724e56fedd89d2/test_Porsche_Macan_GTS-1.jpg?w=640&fm=webp&q=75",
          title: "Test: Porsche Macan GTS – underbart irrationellt",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-porsche-macan-gts-underbart-irrationellt",
          reviewDate: new Date("2026-03-06"),
          fuelType: FuelType.Electric,
        },
      },
      2003299: {
        // Macan Turbo
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/88f03282-07d2-3528-9d34-92f233402d34/04006e65a9e32364dce3745ea588bebc/Porsche-Macan-Turbo-02.jpg?w=640&fm=webp&q=75",
          title: "Test: Elektrisk perfektion i Porsche Macan Turbo",
          reviewUrl: "https://blog.bilbasen.dk/test-elektrisk-perfektion-i-porsche-macan-turbo-477874",
          reviewDate: new Date("2025-02-16"),
        },
      },
    },
    2765: {
      // Panamera-Serie
      2003304: {
        // Panamera Turbo
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/52f4b3f7-1ea0-3536-8b38-fa9205317e51/27132e387e589f045c8935675de5fde9/Porsche-Panamera-Turbo-E-Hybrid-49.jpg?w=640&fm=webp&q=75",
          title: "Test: Ny Porsche Panamera med særdeles aktiv undervogn",
          reviewUrl: "https://blog.bilbasen.dk/test-porsche-panamera-turbo-e-hybrid-474428",
          reviewDate: new Date("2024-11-24"),
        },
      },
    },
  },
  808: {
    // Skoda
    729: {
      // Elroq
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/1a31c434-0aea-3a23-9824-70f429c02d45/04daab4393ba92e47eddd3170a639fd5/TEST-Skoda-Elroq-859992.jpeg?w=640&fm=webp&q=75",
        title: "Test: Skoda Elroq 85 strutter af fornuft og fornemme løsninger",
        reviewUrl: "https://blog.bilbasen.dk/test-skoda-elroq-85-strutter-af-fornuft-og-fornemme-loesninger-478653",
        reviewDate: new Date("2025-03-15"),
        specKeys: ["85"],
      },
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1tiRnNa6nQPiCsmlwb6Ke9/bfb7d50604cd8edee360ef9dd52852fb/Skoda_Elroq_RS.jpg?w=640&fm=webp&q=75",
        title: "Test: Skoda Elroq RS - Skjult familieracer",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-skoda-elroq-rs-skjult-familieracer",
        reviewDate: new Date("2026-03-18"),
        specKeys: ["RS"],
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/7keHQF1DwGMFs1wpCZyrZX/a26eab940afd4a41eb8522c1c6775df1/Skoda-Elroq-10.jpg?w=640&fm=webp&q=75",
        title: "Test: Škoda Elroq provkörd – tjeckiskt förnuft i eldriven form",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-skoda-elroq-provkoerd-tjeckiskt-foernuft-i-eldriven-form",
        reviewDate: new Date("2025-10-24"),
      },
    },
    3545: {
      // Enyaq-Serie
      8395: {
        // Enyaq
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/001e1b4f-3474-3194-b92f-ec8b91ee8697/aeb53845512a08ff37a0a29e9bfa3362/DSC_6841.jpg?w=640&fm=webp&q=75",
          title: "Første tur: Opdateret Skoda Enyaq er ikke mindre elskværdig",
          reviewUrl: "https://blog.bilbasen.dk/foerste-tur-opdateret-skoda-enyaq-er-ikke-mindre-elskvaerdig-479338",
          reviewDate: new Date("2025-03-31"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/3IV5WBDMqi3lkhXB7lKJqm/20a738aa8ac54aec1551973913581006/DSC_9703-1024x683.jpg?w=640&fm=webp&q=75",
          title: "Första åkturen: Uppdaterade Skoda Enyaq är lika älskvärd som tidigare",
          reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/uppdaterade-skoda-enyaq",
          reviewDate: new Date("2025-03-31"),
        },
      },
    },
    3617: {
      // Epiq
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/4bylX8Q52qgzXNlgCNpOXv/8106d5f95415ae8e4e55f435d4fe49a4/DSC_8605_291285e7-1920x1280.jpg?fm=webp&w=640&q=60",
        title: "Første tur: Skoda Epiq er næste kapitel i Skodas elektriske succes",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-skoda-epiq-er-naeste-kapitel-i-skodas-elektriske-succes",
        reviewDate: new Date("2026-02-09"),
      },
    },
    2794: {
      // Superb-Serie
      7532: {
        // Superb
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/90693143-3d10-32b5-b16b-0a2c4ffc19f3/aaedc82f62bf6f073e11fd026cd29298/Skoda-Superb-33.jpg?w=640&fm=webp&q=75",
          title: "Test: Skoda Superb leverer endnu engang",
          reviewUrl: "https://blog.bilbasen.dk/test-skoda-superb-combi-2024-470388",
          reviewDate: new Date("2024-08-22"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/2fVraHHwxOS08MoJQwEA1e/9dc5921e9ae5a05789dfde6673f0af35/Skoda_Superb_Combi__1_.jpg?w=640&fm=webp&q=75",
          title: "Test: Skoda Superb levererar ännu en gång",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-skoda-superb-levererar-aennu-en-gang",
          reviewDate: new Date("2025-01-31"),
        },
      },
    },
    2788: {
      // Octavia-Serie
      1355: {
        // Octavia
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/87829d93-896c-3d86-9af7-ddcf44094cbf/26bc664731757b9b23ef40962936b80c/Skoda-Octavia-2024-COVER.jpg?w=640&fm=webp&q=75",
          title: "Test: Opdateret Skoda Octavia Combi er alt, du behøver",
          reviewUrl: "https://blog.bilbasen.dk/test-opdateret-skoda-octavia-combi-er-alt-du-behoever-471060",
          reviewDate: new Date("2024-09-05"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/3UsBmbqd1KJmaSEsNAmoDp/6b10d1fdf23845ba0fae6a1b808330ea/Skoda_Octavia_Combi__1_.jpg?w=640&fm=webp&q=75",
          title: "Test: Uppdaterad Skoda Octavia Combi är allt du behöver",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-uppdaterad-skoda-octavia-combi-aer-allt-du-behoever",
          reviewDate: new Date("2025-01-31"),
        },
      },
    },
    2793: {
      // Kodiaq-Serie
      2000409: {
        // Kodiaq
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/18a3c748-dbab-32e3-88be-5aa21361a649/41b4916ffb0559c6adf3f5a1c1c08ff3/TEST-Skoda-Kodiaq-15-Selection-MBB-04844.jpeg?w=640&fm=webp&q=75",
          title: "Test: Ny Skoda Kodiaq gør det meste endnu bedre end den gamle",
          reviewUrl: "https://blog.bilbasen.dk/test-ny-skoda-kodiaq-goer-det-meste-endnu-bedre-end-den-gamle-469907",
          reviewDate: new Date("2024-08-10"),
        },
        blocket: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/58rDyW9S7qblZwPf22QFHb/dab7752f098ba97682de887e2fdbb6a8/Skoda_Kodiaq__1_.jpeg?w=640&fm=webp&q=75",
          title: "Test: Nya Skoda Kodiaq gör det mesta ännu bättre än föregångaren",
          reviewUrl:
            "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-nya-skoda-kodiaq-goer-det-mesta-aennu-baettre-aen-foeregangaren",
          reviewDate: new Date("2025-01-31"),
        },
      },
    },
  },
  2369: {
    // Skyworth
    2370: {
      // K
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/36rb4eCiCRgq77a3I5JVFo/3be160730d7ee3444f7f155bf16c1f34/TEST_Skyworth_K_375.jpeg?fm=webp&w=640&q=60",
        title: "Test: Det er useriøst, at Skyworth K sælges i Danmark",
        reviewUrl: "https://blog.bilbasen.dk/test-det-er-useriost-at-skyworth-k-saelges-i-danmark",
        reviewDate: new Date("2026-05-02"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/7eAd0KrVCOVbXrcbLdC7lp/ec2bc388dd7e6bc8577920e1607c363d/Skyworth_K-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Skyworth K – ett osäkert kort",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-skyworth-k-ett-osakert-kort",
        reviewDate: new Date("2026-01-30"),
      },
    },
  },
  811: {
    // Suzuki
    1351: {
      // eVitara
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/2mTJd1BTBxhxGtoQGizIIK/cbf27a98b3176f43f20808b1b1e4d2f3/Suzuki-eVitara-8.jpg?w=640&fm=webp&q=75",
        title: "Test: Suzuki eVitara – Sveriges billigaste fyrhjulsdrivna elbil",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-suzuki-evitara-sveriges-billigaste-fyrhjulsdrivna-elbil",
        reviewDate: new Date("2025-12-30"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/1efXKt1Ym0wJp0v1Ij4fug/9b25458b29ed3f090834acc1225c10a7/Suzuki_e-Vitara-20.jpeg?fm=webp&w=640&q=60",
        title: "Test: Suzuki E Vitara rammer Suzuki-fans – men hvem ellers?",
        reviewUrl: "https://blog.bilbasen.dk/test-suzuki-e-vitara-rammer-suzuki-fans",
        reviewDate: new Date("2026-01-11"),
      },
    },
    1377: {
      // Swift
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/8461719a-c833-34a5-9c02-dc62484200af/132e3820e9b18db585eb46d2b8829fa2/TEST-Suzuki-Swift-CVT-5548.jpeg?w=640&fm=webp&q=75",
        title: "Test: Suzuki Swift med CVT er køreglad, men den har mistet sin prisfordel",
        reviewUrl:
          "https://blog.bilbasen.dk/test-suzuki-swift-med-cvt-er-koereglad-men-den-har-mistet-sin-prisfordel-470649",
        reviewDate: new Date("2024-08-31"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/tgxYnd08g1i8TzO6MGQ40/1b3c247718ffd9150d70d2c1098edae2/Suzuki_Swift__1_.jpeg?w=640&fm=webp&q=75",
        title: "Test: Suzuki Swift med CVT er køreglad, men den har tappat sin prisfördel",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-suzuki-swift-med-cvt-aer-koerglad-men-den-har-tappat-sin-prisfoerdel",
        reviewDate: new Date("2025-01-31"),
      },
    },
  },
  813: {
    // Toyota
    2817: {
      // Aygo-Serie
      2000581: {
        // Aygo X
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/4WPVSgmDvqZ2GFIKvT0ItI/26beb40d6e34b02ffde2a0241b7e3b9c/Toyota_Aygo_X_-_Eksteri%C3%83_r-15.jpg?fm=webp&w=640&q=60",
          title: "Test: Flere kræfter klæder Toyota Aygo X Hybrid – prisen gør ikke",
          reviewUrl: "https://blog.bilbasen.dk/test-flere-kraefter-klaeder-toyota-aygo-x-hybrid",
          reviewDate: new Date("2026-05-10"),
          fuelType: FuelType.HybridPetrol,
        },
      },
    },
    2000575: {
      // bZ4X
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/d0952534-7cc7-3baf-a277-0b6b39bcb0db/46af577a1c0dae6042999064c6ef2390/TEST-Toyota-BZ4X-2024-5874.jpeg?w=640&fm=webp&q=75",
        title: "Test: Nu er Toyota BZ4X en bedre bil",
        reviewUrl: "https://blog.bilbasen.dk/test-nu-er-toyota-bz4x-en-bedre-bil-474088",
        reviewDate: new Date("2024-11-12"),
      },
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4FhB8qSSssdRNtjNJOVI7n/14e8146f861909b11ee55eb62462163f/Toyota_bZ4X-1.jpg?w=640&fm=webp&q=75",
        title: "Toyota bZ4X - Kraftig nivåheving",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/toyota-bz4x-kraftig-nivaheving",
        reviewDate: new Date("2026-02-06"),
      },
    },
    3195: {
      // bZ4X Touring
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/6HrobWpxh2P9WxA70LGA1x/4c79592c36f425f4bcf27c1c10510eac/Toyota_bZ4X_Touring_test.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota bZ4X Touring - Dette blir en bestselger",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-toyota-bz4x-touring-dette-blir-en-bestselger",
        reviewDate: new Date("2026-05-07"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/6HrobWpxh2P9WxA70LGA1x/4c79592c36f425f4bcf27c1c10510eac/Toyota_bZ4X_Touring_test.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota bZ4X Touring – blir en bästsäljare",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-toyota-bz4x-touring-blir-en-bastsaljare",
        reviewDate: new Date("2026-05-12"),
      },
    },
    2000401: {
      // C-HR
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/12f9069b-d4e7-3ea7-b079-4488a22c90f1/432140ecc8a951043e3504facd576b0f/Toyota-C-HR-Plug-in-Hybrid-12.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota C-HR Plug-in Hybrid – letkørt livsstilsbil til en tung pris",
        reviewUrl:
          "https://blog.bilbasen.dk/test-toyota-c-hr-plug-in-hybrid-letkoert-livsstilsbil-til-en-tung-pris-471821",
        reviewDate: new Date("2024-09-22"),
        fuelType: FuelType.PlugInPetrol,
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1PHNKb9ZcS1AvkC6Cb7GZq/6f02c0d64d5f33908926be676f462d74/Toyota_C-HR__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota C-HR Plug-in Hybrid – lättkörd livsstilsbil till ett högt pris",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-toyota-c-hr-plug-in-hybrid-laettkoerd-livsstilsbil-till-ett-hoegt-pris",
        reviewDate: new Date("2025-01-30"),
        fuelType: FuelType.PlugInPetrol,
      },
    },
    2166: {
      // C-HR+
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1VQs9wkOlfvhd2IdDBGPPE/a18d5730bc42f3e7feb912218056b01f/Toyota_C-HR_-37.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota C-HR+, kul Toyota med imponerende rekkevidde",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-toyota-c-hr-kul-toyota-med-imponerende-rekkevidde",
        reviewDate: new Date("2026-03-20"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5jYlgL9jUx8fbPXNM2CXkw/8f6d69d4fe18898e6642aecbe5df8d7a/Toyota_C-HR_-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota C-HR+ – transportmedel för medellånga",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-toyota-c-hr-transportmedel-for-medellanga",
        reviewDate: new Date("2026-03-20"),
      },
    },
    1389: {
      // Corolla
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/cb0748eb-96a8-33e9-a188-8cbe1770ca79/167d837e6ce5366230a66c7abf728c60/Toyota-Corolla-Touring-Sports-GR-Sport-34-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota Corolla Touring Sports med ekstra pift",
        reviewUrl: "https://blog.bilbasen.dk/test-toyota-corolla-touring-sports-455476",
        reviewDate: new Date("2023-10-31"),
      },
    },
    8371: {
      // GR Supra
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/99abba4c-a565-36ba-b953-15d8c5d6d1b6/2e3f9b84600c88bc4e8f08d708b84876/Toyota-GR-Supra-01.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota GR Supra er mere rå og rendyrket med manuelt gear",
        reviewUrl: "https://blog.bilbasen.dk/test-toyota-gr-supra-449277",
        reviewDate: new Date("2023-07-30"),
      },
    },
    1396: {
      // HiLux
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/3UZ2QV1gIWThS5XiDN2LNc/eff2481fe2d2d25c2470250f940af10f/Toyota_Hilux_2026_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota Hilux 2,8 Hybrid 48V – lust för last",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-toyota-hilux-2-8-hybrid-48v-lust-for-last",
        reviewDate: new Date("2026-06-08"),
        specKeys: ["2.8"],
      },
    },
    2818: {
      // Yaris-Serie
      2000534: {
        // GR Yaris
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/cced5262-47d2-3f15-8c3a-3900884d7f10/7178cfb11aa6374e7bcb39c8a4304cb6/TEST-Toyota-GR-Yaris-DAT-MBB-7997.jpeg?w=640&fm=webp&q=75",
          title: "Test: Toyota GR Yaris med automatgear er Årets Sjoveste Bil",
          reviewUrl: "https://blog.bilbasen.dk/test-toyota-gr-yaris-med-automatgear-er-aarets-sjoveste-bil-476738",
          reviewDate: new Date("2025-01-18"),
        },
      },
      3759: {
        // Yaris
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/1fc30041-9fb8-3092-bbfd-7dfd4fc7effe/e73305b9f8f5d782d1f215f75da02901/TEST-Toyota-Yaris-Hybrid-130-811.jpeg?w=640&fm=webp&q=75",
          title: "Test: Der er ekstra skub i Toyota Yaris Hybrid med 130 hk",
          reviewUrl: "https://blog.bilbasen.dk/test-der-er-ekstra-skub-i-toyota-yaris-hybrid-med-130-hk-468848",
          reviewDate: new Date("2024-07-06"),
        },
      },
    },
    1397: {
      // Land Cruiser
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/3f7x9uGXOilEWUjDFOrAuK/ee1fe0e57ae55b7a5c6353cf04747892/Toyota_Land_Cruiser_Limited_Edittion.jpg?w=640&fm=webp&q=75",
        title: "Toyota Land Cruiser 2,8 D Final Edition - King of the road, and off the road.",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/toyota-land-cruiser-2-8-d-final-edition-king-of-the-road-and-off-the-road",
        reviewDate: new Date("2026-01-19"),
      },
    },
    3074: {
      // RAV4
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5e6c4b30-3146-3780-9d35-dfdd3c82a2be/ad80d2199f15309a6d24f7ebfd2a919c/Toyota-Corolla-Cross-01.jpg?w=640&fm=webp&q=75",
        title: "Test: Er motoren et sikkert sted at spare med Toyota Corolla Cross?",
        reviewUrl: "https://blog.bilbasen.dk/test-toyota-corolla-cross-453351",
        reviewDate: new Date("2023-09-24"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4qHx16qg3AnXFsNh51Dke4/e3a15a229fdbf9170702cb2e49d1d343/Toyota_RAV4_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Toyota RAV4 – imponerande institution",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-toyota-rav4-imponerande-institution",
        reviewDate: new Date("2026-04-16"),
      },
    },
    2000118: {
      // Urban Cruiser
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/4GPbAZrDie0ciX6dvln8CK/82c6bbce43d0c8db7620fb56d673cdaf/Toyota_Urban_Cruiser_-_Eksteri%C3%83_r-23.jpg?fm=webp&w=640&q=60",
        title: "Test: Toyota Urban Cruiser er ingen rigtig Toyota",
        reviewUrl: "https://blog.bilbasen.dk/test-toyota-urban-cruiser-er-ingen-rigtig-toyota",
        reviewDate: new Date("2026-05-31"),
        specKeys: ["Executive"],
      },
    },
  },
  817: {
    // Volkswagen
    8376: {
      // ID.3
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4WCNfhKoBJx8e8XEpxbA8W/2ce5db8494d040661de0a592a5259f26/VW_ID__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Uppdaterad VW ID.3 erbjuder fler hästkrafter via abonnemang",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-uppdaterad-vw-id-3-erbjuder-fler-haestkrafter-via-abonnemang",
        reviewDate: new Date("2025-01-30"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/kqcWwE2eq39vDFnsQ6JSf/4117c458e2403b4fb4d78314f2d16b36/VW_ID.3_Fire_Ice_-_Eksteri%C3%83_r-6.jpg?fm=webp&w=640&q=60",
        title: "Test: VW ID.3 Fire+Ice bringer Brian-bilen tilbage",
        reviewUrl: "https://blog.bilbasen.dk/test-vw-id-3-fire-ice-bringer-brian-bilen-tilbage",
        reviewDate: new Date("2026-03-15"),
      },
    },
    2000553: {
      // ID.3 GTX
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/1d089231-34d8-32c4-a97b-4447c61a45d5/198c3585932b39338a82fab9a591280d/TEST-VW-ID.3-GTX-MBB-821.jpeg?w=640&fm=webp&q=75",
        title: "Test: Volkswagen ID.3 GTX Performance+ folder sig ud på de lange ture",
        reviewUrl:
          "https://blog.bilbasen.dk/test-volkswagen-id-3-gtx-performance-folder-sig-ud-paa-de-lange-ture-men-kald-den-ikke-en-gti-475038",
        reviewDate: new Date("2025-01-04"),
      },
    },
    2000637: {
      // ID.7
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/24a814fb-cd79-357c-b53d-7a90b9c764c9/9eaf569da64bbd06cc8ab29803398fe3/VW-ID.7-Tourer-43.jpg?w=640&fm=webp&q=75",
        title: "Test: VW ID.7 Tourer er en stationcar, der sparker opad",
        reviewUrl: "https://blog.bilbasen.dk/test-vw-id-7-tourer-er-en-stationcar-der-sparker-opad-473826",
        reviewDate: new Date("2024-11-04"),
        specKeys: ["Tourer"],
      },
    },
    8400: {
      // ID. Buzz
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/3461aff4-ec10-3ebb-9869-e7ac895254eb/2641559be5013d9dae6d5cda502d01f7/TEST-VW-ID.-Buzz-GTX-MBB-384.jpeg?w=640&fm=webp&q=75",
        title: "Test: VW ID. Buzz GTX er en ulv i fåreklæder",
        reviewUrl: "https://blog.bilbasen.dk/test-vw-id-buzz-gtx-er-en-ulv-i-faareklaeder-479143",
        reviewDate: new Date("2025-03-29"),
      },
    },
    8392: {
      // ID.4
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/eec3a87b-81d9-321e-85be-804b350dc6cf/b6e5386b6e13484693494ea96306acc5/VW-ID.4-Pro-34.jpg?w=640&fm=webp&q=75",
        title: "Test: Kløgtig opdatering bringer VW ID.4 i front",
        reviewUrl: "https://blog.bilbasen.dk/test-kloegtig-opdatering-bringer-vw-id-4-i-front-467535",
        reviewDate: new Date("2024-06-06"),
      },
    },
    2835: {
      // Passat-Serie
      1437: {
        // Passat
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/22be4c84-943a-3ea1-b98f-499fb5a4ac77/2719365f708804ec7cc819529ac7a7d4/VW-Passat-Variant-01.jpg?w=640&fm=webp&q=75",
          title: "Test: Ny VW Passat har mere end 50 års erfaring",
          reviewUrl: "https://blog.bilbasen.dk/test-vw-passat-variant-2024-465788",
          reviewDate: new Date("2024-06-13"),
        },
      },
    },
    2834: {
      // Tiguan-Serie
      8329: {
        // Tiguan
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/695d7b7e-46c1-3b25-a959-84c5e8d704ae/fa234f4bdf477072fdee9cf657ab805d/TEST-VW-Tiguan-15-836.jpeg?w=640&fm=webp&q=75",
          title: "Test: VW har priset Tiguan ud af markedet",
          reviewUrl: "https://blog.bilbasen.dk/test-vw-har-priset-tiguan-ud-af-markedet-467297",
          reviewDate: new Date("2024-06-01"),
        },
      },
    },
    2000504: {
      // T-Cross
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5ada8c61-f54d-3892-b22b-453788624667/2e76c8e37db1cd2b5fd1338fd04d7b8e/VW-T-Cross-23.jpg?w=640&fm=webp&q=75",
        title: "Test: Forbedret VW T-Cross er et bundsolidt basisprodukt",
        reviewUrl: "https://blog.bilbasen.dk/test-forbedret-vw-t-cross-er-et-bundsolidt-basisprodukt-467621",
        reviewDate: new Date("2024-06-09"),
      },
    },
  },
  818: {
    // Volvo
    1375: {
      // ES90
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5b70ltE2S2l5OrjmgI8Ccu/c7fcd5edc066ce1776799233168692f8/Volvo_ES90-test-.jpg?w=640&fm=webp&q=75",
        title: "Volvo ES90 - skivebom fra rutinert SUV-produsent",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/volvo-es90-skivebom-fra-rutinert-suv-produsent",
        reviewDate: new Date("2026-04-27"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/2XColuDVTkHHKuoGQ2s9Ud/cb69774b6908632246ae13617fc94083/TEST_Volvo_ES90_RWD_MBB_321.jpeg?fm=webp&w=640&q=60",
        title: "Test: Volvo ES90 er høj på komfort",
        reviewUrl: "https://blog.bilbasen.dk/test-volvo-es90-er-hoj-pa-komfort",
        reviewDate: new Date("2026-02-21"),
      },
    },
    3168: {
      // EX60
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/57IsvKpc5DIBnwujHupVGW/5e317b6f76df15034412beddef4b5a26/Volvo_EX60_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Volvo EX60 – chassit avgör saken",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-volvo-ex60-chassit-avgor-saken",
        reviewDate: new Date("2026-05-20"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/6ZdirF2jZOlGQJa2ihh1Yg/7f3aea03424d441ccf0c77d6929b9bf8/20251214_EX60_MEDIA_003.jpeg?fm=webp&w=640&q=60",
        title: "Video: Alt du skal vide om Volvo EX60",
        reviewUrl: "https://blog.bilbasen.dk/video-alt-du-skal-vide-om-volvo-ex60",
        reviewDate: new Date("2026-01-21"),
      },
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/cUvZ3ZlcDKFyiwMLOE8tV/dffd291924748abeda4ed0082500376e/Volvo_EX60_test-9.jpg?w=640&fm=webp&q=75",
        title: "Test: Volvo EX60 - Første tur",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-volvo-ex60-forste-tur",
        reviewDate: new Date("2026-06-01"),
      },
    },
    2003622: {
      // EX90
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5HKtaNYfEnYjlOxwhUc1Ve/9110d262fabca5a914ef127e13c8b6cb/Volvo_EX90-33.jpg?w=640&fm=webp&q=75",
        title: "Test: Volvo EX90 er en tung drøm på strøm",
        reviewUrl: "https://blog.bilbasen.dk/test-volvo-ex90",
        reviewDate: new Date("2025-05-25"),
      },
    },
    7651: {
      // XC90
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/7f3O6XMhdNbXYqDlTeZbPP/33aaee2ac60aebe9e05d5513f46cc7f1/TEST_Volvo_XC90_T8_MBB_52.jpeg?w=640&fm=webp&q=75",
        title: "Test: Farvel til Volvo XC90",
        reviewUrl: "https://blog.bilbasen.dk/test-farvel-til-volvo-xc90",
        reviewDate: new Date("2025-04-19"),
      },
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/7Heu0h6unff57aSwUKQE9k/f91e6c993d62182d490477a0eccc3702/Volvo_XC90.jpg?w=640&fm=webp&q=75",
        title: "Test: Volvo XC90 - Eksklusiv og (nesten) perfekt på langtur",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-volvo-xc90-eksklusiv-og-nesten-perfekt-pa-langtur",
        reviewDate: new Date("2026-04-05"),
      },
    },
    2000653: {
      // EX30
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/904973d3-0658-351e-9ed7-54d2f18f828b/ea7fc58849d1dec5236940c41048ba1b/Volvo-EX30-Eksterioer-05.jpg?w=640&fm=webp&q=75",
        title: "Test: Volvo EX30 er ikke en Volvo, som du husker dem",
        reviewUrl: "https://blog.bilbasen.dk/test-volvo-ex30-er-ikke-en-volvo-som-du-husker-dem-464963",
        reviewDate: new Date("2024-04-14"),
      },
    },
    2000674: {
      // EC40
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5ed2544d-f885-34f4-8591-1aaf3b96fdb9/af7592115e729552acae040c16433ce0/Volvo-EC40-Single-Motor-Extended-Range-02.jpg?w=640&fm=webp&q=75",
        title: "Test: Volvo EC40 skal vælges med stort batteri og én elmotor",
        reviewUrl: "https://blog.bilbasen.dk/test-volvo-ec40-skal-vaelges-med-stort-batteri-og-en-elmotor-465322",
        reviewDate: new Date("2024-04-21"),
      },
    },
  },
  8078: {
    // Tesla
    2000501: {
      // Model 3
      bilbasen: [
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/ab3777bb-80ce-355e-a8a1-d5649f161a1a/78deda22281f99c56ef7318c668f56bb/Tesla-Model-3-Performance-Eksterioer-31.jpg?w=640&fm=webp&q=75",
          title: "Test: Tesla Model 3 Performance er en supersedan til spotpris",
          reviewUrl: "https://blog.bilbasen.dk/test-tesla-model-3-performance-er-en-supersedan-til-spotpris-470541",
          reviewDate: new Date("2024-08-25"),
          specKeys: ["Performance"],
        },
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/4Luq1rda65dWC4MWvjO7E5/d9db2526462965d5ddda2f2c9b9c5b3b/Tesla_Model_3_RWD.jpg?fm=webp&w=640&q=60",
          title: "Test: Den billigste Tesla Model 3 smadrer søstermodellen",
          reviewUrl: "https://blog.bilbasen.dk/test-den-billigste-tesla-model-3-smadrer-sostermodellen",
          reviewDate: new Date("2026-04-19"),
          specKeys: ["RWD"],
        },
      ],
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/5BE21dYnxWdMLRHMhhqrv5/078730bb80a52a2660ce505c529c8af8/Tesla_Model_3_Performance-6.jpg?w=640&fm=webp&q=75",
        title: "Test: Tesla Model 3 Performance - sportslig, sprek og underholdene",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/tesla-model-3-performance-sportslig-sprek-og-underholdene",
        reviewDate: new Date("2026-04-14"),
        specKeys: ["Performance"],
      },
    },
    2000555: {
      // Model Y
      bilbasen: [
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/1c5a4b81-6da4-30cc-ad33-e5d128f9f8d8/d888f75cb24ae674c95fd16a5dfa2ece/Tesla-Model-Y-Juniper-09.jpg?w=640&fm=webp&q=75",
          title: "Første tur: Forandring fryder for folkefavoritten Tesla Model Y",
          reviewUrl: "https://blog.bilbasen.dk/foerste-tur-forandring-fryder-for-folkefavoritten-tesla-model-y-478800",
          reviewDate: new Date("2025-03-16"),
          specKeys: ["Long Range", "AWD"],
        },
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/5lHKl1xXOvjWSrR2Oj2t6V/db5674a51559042ebae63cd6df306dc8/Tesla_Model_Y_Baghjulstr%C3%83_k-14.jpg?fm=webp&w=640&q=60",
          title: "Test: Kompromisserne mærkes i den billigste Tesla Model Y",
          reviewUrl: "https://blog.bilbasen.dk/test-kompromisserne-maerkes-i-den-billigste-tesla-model-y",
          reviewDate: new Date("2026-02-15"),
          specKeys: ["RWD"],
        },
      ],
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/22VonN7Z2CIkjVd0ZALCZ1/2e15aa942b13d923e1331ef4eaabf6e2/Tesla_Model_Y_Premium_Long_Range-25.jpg?w=640&fm=webp&q=75",
        title: "Tesla Model Y Premium Long Range – Imponerende god!",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/tesla-model-y-premium-long-range-imponerende-god",
        reviewDate: new Date("2025-11-27"),
        specKeys: ["Long Range", "AWD"],
      },
    },
  },
  792: {
    // Nissan
    2000584: {
      // Ariya
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/5aa58179-8476-3e1a-b7a6-91ec3375cb9a/b9b7d3ef21f0542b9ca824e74310dde1/Nissan-Ariya-Evolve-16.jpg?w=640&fm=webp&q=75",
        title: "Test: Giver den lækeste og mest kraftfulde Nissan Ariya mening?",
        reviewUrl: "https://blog.bilbasen.dk/test-giver-den-laekreste-og-mest-kraftfulde-nissan-ariya-mening-469119",
        reviewDate: new Date("2024-06-14"),
      },
    },
    2000175: {
      // Juke
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/4738daa3-df35-3162-b307-b10d67662562/e05e69931e2f181387770c3f38d671f4/Nissan-Juke-Hybrid-21.jpg?w=640&fm=webp&q=75",
        title: "Test: Festfyrværkeriet udebliver i farverig Nissan Juke Hybrid",
        reviewUrl: "https://blog.bilbasen.dk/test-festfyrvaerkeriet-udebliver-i-farverig-nissan-juke-hybrid-472731",
        reviewDate: new Date("2024-10-13"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/SUu2lzs20Kz4aY937gqW5/4d5f4b346f7043e6901b91e00466a21e/Nissan_Juke__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Fyrverkeriet utebliver i färgstarka Nissan Juke Hybrid",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-fyrverkeriet-uteblir-i-faergstarka-nissan-juke-hybrid",
        reviewDate: new Date("2025-01-30"),
      },
    },
    2737: {
      // Micra-Serie
      1223: {
        // Micra
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/1WZDcsut1sDJxY9OHFmB5r/7a4a510c79ffe91519e52e2dfb1db163/Nissan_Micra-eksteri%C3%83_r--20.jpg?fm=webp&w=640&q=60",
          title: "Test: Vælg gerne Nissan Micra",
          reviewUrl: "https://blog.bilbasen.dk/test-vaelg-gerne-nissan-micra",
          reviewDate: new Date("2026-03-26"),
        },
      },
    },
    7188: {
      // X-Trail
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/dab4c909-5fa9-3208-bca2-3b6c012b46e2/96cb0aa6c3d015856e59f3b744edbcb6/TEST-Nissan-X-Trail-15-MHEV-618.jpeg?w=640&fm=webp&q=75",
        title: "Test: Nissan X-Trail gør sig bedst som hybrid",
        reviewUrl: "https://blog.bilbasen.dk/test-nissan-x-trail-goer-det-bedst-som-hybrid-465972",
        reviewDate: new Date("2024-05-04"),
      },
    },
  },
  804: {
    // Renault
    2000268: {
      // Captur
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/9c1e9c89-56a2-3db0-b70d-f76b3201a95b/e48ac3a2f1c7b6d8beb09d41cc105280/Renault-Captur-Facelift-15.jpg?w=640&fm=webp&q=75",
        title: "Test: Omfattende facelift gør Renault Captur tidssvarende",
        reviewUrl: "https://blog.bilbasen.dk/test-omfattende-facelift-goer-renault-captur-tidssvarende-474604",
        reviewDate: new Date("2024-11-25"),
      },
    },
    2773: {
      // Clio-Serie
      1322: {
        // Clio
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/66ea2eca-8dab-3c73-a1f3-deaee31730b9/f9cb24c8eacd1884df4fcb8d09280620/Renault-Clio-22.jpg?w=640&fm=webp&q=75",
          title: "Test: Opdateret Renault Clio er stadig meget bil for pengene",
          reviewUrl: "https://blog.bilbasen.dk/test-opdateret-renault-clio-er-stadig-meget-bil-for-pengene-467886",
          reviewDate: new Date("2024-06-17"),
        },
      },
    },
    2798: {
      // Rafale
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/26I7ZBHaRBa6L9TdLeurmy/80ca9fdf65b9674cabd12fd50a97032f/Renault_Rafale_test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Renault Rafale – teknikens värld",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-renault-rafale-teknikens-varld",
        reviewDate: new Date("2026-05-29"),
      },
    },
    7782: {
      // Twingo
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/L496nHJywAKODYgvLCz6x/f4635a303f16fcda476d7c4fe7b8a200/Renault-Twingo-5.jpg?w=640&fm=webp&q=75",
        title: "Test: Renault Twingo - fransk smågodt til lavpris",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-renault-twingo-fransk-smagodt-til-lavpris",
        reviewDate: new Date("2026-03-28"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4aKMV0pEWmBCEb46iTOcgR/3f446d3a14de9e5773b4b0996a55e8e0/Renault-Twingo-test-1.jpg?w=640&fm=webp&q=75",
        title: "Test: Renault Twingo E-Tech – enkelt glädjeämne",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-renault-twingo-e-tech-enkelt-gladjeamne",
        reviewDate: new Date("2026-03-27"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/31i5Z1flbL1yXOB9XL1fAH/ec0c3cfb002fac8b2b2b134aa28577b3/RenaultTwingoE-TechElectric_AbsolutGreen_Action%C3%82_ThomasCortesi-4.jpeg?fm=webp&w=640&q=60",
        title: "Første tur: Simple glæder i Renault Twingo E-Tech",
        reviewUrl: "https://blog.bilbasen.dk/forste-tur-simple-glaeder-i-renault-twingo-e-tech",
        reviewDate: new Date("2026-03-23"),
      },
    },
    699: {
      // 5 E-Tech
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/1ZVfrkkz4EbB4xcwAg6Ze3/bb7b44d8d00705a8fde8fb290cc4ca8b/Renault_5_E-tech-5.jpg?w=640&fm=webp&q=75",
        title: "Renault 5 E-tech - fransk sjokolade, søt og smakfull",
        reviewUrl:
          "https://www.finn.no/bilguiden/tester-og-anmeldelser/renault-5-e-tech-fransk-sjokolade-sot-og-smakfull",
        reviewDate: new Date("2025-12-05"),
      },
    },
  },
  796: {
    // Peugeot
    2754: {
      // 2008-Serie
      2000259: {
        // 2008
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/b50085fc-1b27-3cb9-b6cf-266ead7d0647/e7eb402b20fb2a6c5b44a06f50aeedcb/Peugeot-2008-FL-29.jpg?w=640&fm=webp&q=75",
          title: "Test: Peugeot 2008 med skarpere klør end de fleste",
          reviewUrl: "https://blog.bilbasen.dk/test-peugeot-2008-fl-2023-453450",
          reviewDate: new Date("2023-09-26"),
        },
      },
    },
    2758: {
      // 308-Serie
      2000659: {
        // e-308
        bilbasen: {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/2acec741-20bb-3080-8fe0-089b1b39448b/63eb60acb1e6be9060f442177b75adea/TEST-Peugeot-E-308-SW-754.jpeg?w=640&fm=webp&q=75",
          title: "Test: Peugeot E-308 SW er god, men ikke god nok",
          reviewUrl: "https://blog.bilbasen.dk/test-peugeot-e-308-sw-er-god-men-ikke-god-nok-462516",
          reviewDate: new Date("2024-03-02"),
        },
      },
    },
    2752: {
      // 3008-Serie
      2003181: {
        // e-3008
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/74WE2Qz8dOmZz8cEm4u8C0/a1eab1fbe4c19bf42a02b2d8734b710f/Peugeot_E-3008.jpg?w=640&fm=webp&q=75",
          title: "Peugeot E-3008 4WD - Peugeot på myke løvepoter",
          reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/peugeot-e-3008-4wd-peugeot-pa-myke-lovepoter",
          reviewDate: new Date("2025-11-23"),
        },
      },
    },
    2760: {
      // 5008-Serie
      2003182: {
        // e-5008
        finn: {
          thumbnailUrl:
            "https://images.ctfassets.net/3xdpcmkzm6t5/6BHybkXAP4L7iZorsb6c1I/a1aa8da14b0d93fc4da9e4a191b21e97/Peugeot_E-5008-11.jpg?w=640&fm=webp&q=75",
          title: "Peugeot E-5008 4WD - Tøff, komfortabel og fleksibel",
          reviewUrl:
            "https://www.finn.no/bilguiden/tester-og-anmeldelser/peugeot-e-5008-4wd-toff-komfortabel-og-fleksibel",
          reviewDate: new Date("2025-11-17"),
        },
      },
    },
  },
  8102: {
    // Polestar
    8370: {
      // 2
      bilbasen: [
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/e59c4d86-a08e-31a7-83dc-055425dc9ff6/60e4d33aafbb3538f355b4d48d3c6cb4/Polestar-2-Facelift-18.jpg?w=640&fm=webp&q=75",
          title: "Test: Polestar 2 tager kegler med kløgtig opdatering",
          reviewUrl: "https://blog.bilbasen.dk/test-polestar-2-tager-kegler-med-kloegtig-opdatering-454864",
          reviewDate: new Date("2023-10-17"),
          specKeys: ["Long range"],
        },
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/4ik3ql58Qf3d6WvzSUw5mc/3a4759e94a4095819332a62013d5fb27/Polestar_2_Performance_-_Eksteri%C3%83_r-13.jpg?fm=webp&w=640&q=60",
          title: "Test: Polestar 2 Performance er en ven i sneen",
          reviewUrl: "https://blog.bilbasen.dk/test-polestar-2-performance-er-en-ven-i-sneen",
          reviewDate: new Date("2026-02-08"),
          specKeys: ["Performance"],
        },
      ],
    },
    2003206: {
      // 3
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4FIjrD3CF1ImTySL1lTo0C/1bac619edebb4fd61db479606bfa2485/Polestar_3_test-5.jpg?w=640&fm=webp&q=75",
        title: "Test: Polestar 3 - Balansert underholdningsmaskin",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-polestar-3-balansert-underholdningsmaskin",
        reviewDate: new Date("2026-05-05"),
      },
    },
    2003207: {
      // 4
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/48W4lSOuRqnXdFejcjE9W5/ac1fd6e6d683221078b70e20f49d724e/Polestar_4_test-38.jpg?w=640&fm=webp&q=75",
        title: "Test: Polestar 4 - Fem stjernes GT",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/test-polestar-4-fem-stjernes-gt",
        reviewDate: new Date("2026-05-19"),
      },
    },
  },
  8104: {
    // XPeng
    2003634: {
      // G6
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/2VdCQgyUk5nAjRpU5SWZcR/dc0cb2adef95eccebde8dbd77cc5da6e/Xpeng_G6__1_.jpg?w=640&fm=webp&q=75",
        title: "Xpeng G6 slår alla men blir snabbt vardag",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/xpeng-g6-slar-alla-men-blir-snabbt-vardag",
        reviewDate: new Date("2025-01-30"),
      },
      bilbasen: {
        thumbnailUrl:
          "https://images.ctfassets.net/8nclzaiq1nmu/7voWa3nQbe672BIXDVa5QE/a921a8edf13ecb316aa1b88afa235cf7/TEST_XPeng_G6_LR_2026_MBB_8948.jpeg?fm=webp&w=640&q=60",
        title: "Test: Faceliftet XPeng G6 Long Range er bilernes svar på havregryn",
        reviewUrl: "https://blog.bilbasen.dk/test-faceliftet-xpeng-g6-long-range-er-bilernes-svar-pa-havregryn",
        reviewDate: new Date("2026-04-04"),
      },
    },
    2000573: {
      // G9
      bilbasen: [
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/485e8a71-07d4-31b6-8f12-a12aa18d9bad/cdae58232665064910350fe1a0b0749b/XPeng-P7-Wing-31.jpg?w=640&fm=webp&q=75",
          title: "Test: Opdateret XPeng P7 til europæerne",
          reviewUrl: "https://blog.bilbasen.dk/test-xpeng-p7-wing-edition-2-447149",
          reviewDate: new Date("2023-06-29"),
        },
        {
          thumbnailUrl:
            "https://images.ctfassets.net/8nclzaiq1nmu/IQ8apHOd81O5KeRq8EDnM/3eb80f5675b80d9be122111e95dca807/Test_XPeng_G9_Performance_MBB_865.jpeg?fm=webp&w=640&q=60",
          title: "Test: Forfinet XPeng G9 Performance er luksus og lynladning, men den er ikke længere unik",
          reviewUrl:
            "https://blog.bilbasen.dk/test-forfinet-xpeng-g9-performance-er-luksus-og-lynladning-men-den-er-ikke",
          reviewDate: new Date("2026-02-28"),
        },
      ],
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/4hUExTO5jFY3JXLredFZs6/99c8ea0d65d9b6bbd037e9442e1bf8a4/XPeng_G9-1.jpg?w=640&fm=webp&q=75",
        title: "XPeng G9 - Romslig, komfortabel og rask",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/xpeng-g9-romslig-komfortabel-og-rask",
        reviewDate: new Date("2026-02-20"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/45M4AG6U9kdmhqNLfZJoLS/39197ea6ecf4a3f057e6c71aa4b3a302/XPeng_G9__1_.jpg?w=640&fm=webp&q=75",
        title: "Test: Uppdaterad Xpeng G9 med efterlängtad utrustning",
        reviewUrl:
          "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-uppdaterad-xpeng-g9-med-efterlaengtad-utrustning",
        reviewDate: new Date("2025-01-17"),
      },
    },
    3125: {
      // P7+
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/3Y9bAiflLkPUYAlAdFexxw/782678883c7054296923b3dc7840ceac/Xpeng_P7__test-3.jpg?w=640&fm=webp&q=75",
        title: "Xpeng P7+ – Luksuskjempe med enorm ladeeffekt",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/xpeng-p7-luksuskjempe-med-enorm-ladeeffekt",
        reviewDate: new Date("2025-05-25"),
      },
      blocket: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/6sMdPHerI3zvZetAykLpLj/70200a2b56a16ffbbe4b040e8061f1ae/Xpeng_P7__test.jpg?w=640&fm=webp&q=75",
        title: "Test: Xpeng P7+ – en laddsnabb lyxjätte",
        reviewUrl: "https://www.blocket.se/bilguiden/biltester-och-provkorningar/test-xpeng-p7-en-laddsnabb-lyxjatte",
        reviewDate: new Date("2026-06-02"),
      },
    },
  },
  200841: {
    // Zeekr
    1367: {
      // 7X
      finn: {
        thumbnailUrl:
          "https://images.ctfassets.net/3xdpcmkzm6t5/lSAjq8xd4KkQkqpTY3Kgp/8028f0a5a939a8dd05be12d279e2575c/Zeekr_7X-39.jpg?w=640&fm=webp&q=75",
        title: "Test: Zeekr 7X - Mye luksus for pengene",
        reviewUrl: "https://www.finn.no/bilguiden/tester-og-anmeldelser/zeekr-7x-mye-luksus-for-pengene",
        reviewDate: new Date("2026-03-08"),
      },
    },
  },
};
